import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import posthog from 'posthog-js';

const BLUE  = 'var(--signal)';
const TEXT1 = 'var(--ink)';
const TEXT2 = 'var(--slate)';
const TEXT3 = 'var(--mute)';

const FILING_STATUSES = [
  { label: 'Single',                      value: 'single' },
  { label: 'Married Filing Jointly',       value: 'married filing jointly' },
  { label: 'Married Filing Separately',    value: 'married filing separately' },
  { label: 'Head of Household',            value: 'head of household' },
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

function StickyHeader() {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px',
    }}>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div style={{ height: 3, backgroundColor: 'rgba(0,0,0,0.07)' }}>
      <div style={{ height: '100%', width: `${pct}%`, backgroundColor: BLUE, transition: 'width 0.3s ease' }} />
    </div>
  );
}

function FocusInput({ value, onChange, placeholder, type = 'text' }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: TEXT1,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  );
}

function FocusSelect({ value, onChange, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: value ? TEXT1 : TEXT3,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        cursor: 'pointer',
      }}
    >
      {children}
    </select>
  );
}

function Field({ label, optional, helper, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: TEXT1 }}>{label}</span>
        {optional && <span style={{ fontSize: 10, color: TEXT3 }}>Optional</span>}
      </div>
      {children}
      {helper && (
        <p style={{ margin: '3px 0 0', fontSize: 10, color: TEXT3, lineHeight: 1.5 }}>
          {helper}
        </p>
      )}
    </div>
  );
}

export default function TaxSetup() {
  const navigate = useNavigate();
  const [annualIncome, setAnnualIncome]           = useState('');
  const [businessExpenses, setBusinessExpenses]   = useState('');
  const [filingStatus, setFilingStatus]           = useState('');
  const [state, setState]                         = useState('');
  const [priorYearTax, setPriorYearTax]           = useState('');
  const [error, setError]                         = useState('');
  const [loading, setLoading]                     = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!annualIncome || !businessExpenses || !filingStatus || !state) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/tax/setup', {
        annual_income:      parseFloat(annualIncome),
        business_expenses:  parseFloat(businessExpenses),
        filing_status:      filingStatus,
        state,
        prior_year_tax:     priorYearTax ? parseFloat(priorYearTax) : null,
      });
      posthog.capture('tax_setup_completed');
      navigate('/onboarding/curve');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
      <StickyHeader />
      <ProgressBar pct={50} />

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px' }}>
        <p style={{ margin: '0 0 4px', fontSize: 11, color: TEXT3 }}>Step 2 of 4</p>
        <h1 style={{ margin: '0 0 8px', fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: TEXT1 }}>Your tax setup</h1>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
          Takes about 2 minutes. We'll calculate your exact quarterly reserve.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <Field label="Gross annual income" helper="Your best estimate for total 1099 income this year">
            <FocusInput
              type="number"
              value={annualIncome}
              onChange={(e) => setAnnualIncome(e.target.value)}
              placeholder="e.g. 80000"
            />
          </Field>

          <Field label="Annual business expenses" helper="Tools, fuel, insurance, phone — anything to do your job">
            <FocusInput
              type="number"
              value={businessExpenses}
              onChange={(e) => setBusinessExpenses(e.target.value)}
              placeholder="e.g. 12000"
            />
          </Field>

          <Field label="Filing status">
            <FocusSelect value={filingStatus} onChange={(e) => setFilingStatus(e.target.value)}>
              <option value="" disabled>Select filing status…</option>
              {FILING_STATUSES.map(({ label, value }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </FocusSelect>
          </Field>

          <Field label="State">
            <FocusSelect value={state} onChange={(e) => setState(e.target.value)}>
              <option value="" disabled>Select your state…</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </FocusSelect>
          </Field>

          <Field
            label="Last year's total tax paid"
            optional
            helper="We'll factor it into your reserve target so a strong year doesn't catch you short"
          >
            <FocusInput
              type="number"
              value={priorYearTax}
              onChange={(e) => setPriorYearTax(e.target.value)}
              placeholder="e.g. 18000"
            />
          </Field>

          {error && (
            <p style={{
              color: 'var(--stop)', fontSize: 13,
              margin: '0 0 12px', padding: '10px 12px',
              backgroundColor: 'var(--stop-soft)', border: '1px solid var(--stop-border)',
              borderRadius: 8,
            }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              backgroundColor: loading ? 'var(--signal-disabled)' : BLUE,
              color: 'var(--on-signal)', borderRadius: 10,
              padding: 13, fontSize: 14, fontWeight: 500,
              border: 'none', cursor: loading ? 'default' : 'pointer',
              marginTop: 8,
            }}
          >
            {loading ? 'Calculating…' : 'Calculate my reserve →'}
          </button>
        </form>
      </div>
    </div>
  );
}
